import React, { useState } from "react";
import { alumnosProfesorService } from "../../../services/profesor/alumnos.service";
import { cursosProfesorService } from "../../../services/profesor/cursos.service";
import { CursoProfesor } from "../cursos/dto/cursos.dto";

interface CursosTableProps {
  cursos: CursoProfesor[];
  onRefresh: () => void;
  loading?: boolean;
}

interface NotificationState {
  isOpen: boolean;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
}

export const CursosTable: React.FC<CursosTableProps> = ({
  cursos,
  onRefresh,
  loading = false,
}) => {
  const [selectedCurso, setSelectedCurso] = useState<CursoProfesor | null>(
    null
  );
  const [modalAbierto, setModalAbierto] = useState(false);
  const [accion, setAccion] = useState<"editar" | "eliminar" | "crear" | null>(
    null
  );
  const [nuevoCurso, setNuevoCurso] = useState({
    nombre: "",
    anio: new Date().getFullYear(),
    anioEscolarId: null as number | null,
    vigente: true,
  });
  const [aniosEscolares, setAniosEscolares] = useState<
    { idAnioEscolar: number; nombre: string; orden: number }[]
  >([]);
  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    type: "success",
    title: "",
    message: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  const showNotification = (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message: string
  ) => {
    setNotification({ isOpen: true, type, title, message });
    setTimeout(() => {
      setNotification({ isOpen: false, type, title: "", message: "" });
    }, 4000);
  };

  const getErrorMessage = (error: any): { title: string; message: string } => {
    if (error?.response?.data) {
      const errorData = error.response.data;

      if (errorData.error) {
        return {
          title: "Error",
          message: errorData.error,
        };
      }

      if (errorData.message) {
        return {
          title: "Error",
          message: errorData.message,
        };
      }

      if (errorData.detalles && Array.isArray(errorData.detalles)) {
        const detalles = errorData.detalles
          .map((d: any) => {
            if (typeof d === "string") return d;
            if (d.message) return d.message;
            if (d.path && d.message) return `${d.path.join(".")}: ${d.message}`;
            return JSON.stringify(d);
          })
          .join(", ");
        return {
          title: "Error de validación",
          message: detalles || "Los datos ingresados no son válidos.",
        };
      }
    }

    if (
      error?.message?.includes("Network Error") ||
      error?.code === "ERR_NETWORK"
    ) {
      return {
        title: "Error de conexión",
        message:
          "No se pudo conectar con el servidor. Verifica tu conexión a internet.",
      };
    }

    if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
      return {
        title: "Tiempo de espera agotado",
        message: "La solicitud tardó demasiado. Por favor, intenta nuevamente.",
      };
    }

    if (error?.response?.status === 400) {
      return {
        title: "Datos inválidos",
        message:
          error?.response?.data?.error || "Los datos enviados no son válidos.",
      };
    }

    if (error?.response?.status === 401) {
      return {
        title: "No autorizado",
        message: "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.",
      };
    }

    if (error?.response?.status === 403) {
      return {
        title: "Acceso denegado",
        message: "No tienes permisos para realizar esta acción.",
      };
    }

    if (error?.response?.status === 404) {
      return {
        title: "No encontrado",
        message:
          error?.response?.data?.error || "El recurso solicitado no existe.",
      };
    }

    if (error?.response?.status === 409) {
      return {
        title: "Conflicto",
        message:
          error?.response?.data?.error ||
          "Ya existe un registro con estos datos.",
      };
    }

    if (error?.response?.status === 422) {
      return {
        title: "Error de validación",
        message:
          error?.response?.data?.error ||
          "Los datos no cumplen con los requisitos.",
      };
    }

    if (error?.response?.status === 500) {
      return {
        title: "Error del servidor",
        message:
          "Ocurrió un error en el servidor. Por favor, intenta más tarde.",
      };
    }

    if (error?.message) {
      return {
        title: "Error",
        message: error.message,
      };
    }
    return {
      title: "Error inesperado",
      message:
        "Ocurrió un error inesperado. Por favor, intenta nuevamente o contacta al soporte.",
    };
  };

  const abrirModal = (curso: CursoProfesor, tipo: "editar" | "eliminar") => {
    setSelectedCurso(curso);
    setAccion(tipo);
    setModalAbierto(true);
  };

  const abrirModalCrear = async () => {
    setAccion("crear");
    setModalAbierto(true);
    setNuevoCurso({
      nombre: "",
      anio: new Date().getFullYear(),
      anioEscolarId: null,
      vigente: true,
    });
    try {
      const aniosData = await alumnosProfesorService.getAniosEscolares();
      setAniosEscolares(aniosData);
    } catch (error: any) {
      console.error("Error cargando años escolares:", error);
      const errorInfo = getErrorMessage(error);
      showNotification("error", errorInfo.title, errorInfo.message);
    }
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setSelectedCurso(null);
    setAccion(null);
    setNuevoCurso({
      nombre: "",
      anio: new Date().getFullYear(),
      anioEscolarId: null,
      vigente: true,
    });
  };

  const handleConfirmar = async () => {
    if (accion === "crear") {
      if (!nuevoCurso.nombre || !nuevoCurso.anioEscolarId || !nuevoCurso.anio) {
        showNotification(
          "error",
          "Campos requeridos",
          "Por favor completa todos los campos obligatorios."
        );
        return;
      }
    }
    if (accion !== "crear" && !selectedCurso) return;

    try {
      setIsSaving(true);

      if (accion === "crear") {
        const response = await cursosProfesorService.createCurso({
          nombre: nuevoCurso.nombre,
          anio: nuevoCurso.anio,
          anioEscolarId: nuevoCurso.anioEscolarId!,
          vigente: nuevoCurso.vigente ?? true,
        });

        if (response.success) {
          cerrarModal();
          await new Promise((resolve) => setTimeout(resolve, 300));
          await onRefresh();
          showNotification(
            "success",
            "Curso creado",
            "El curso ha sido registrado correctamente."
          );
        } else {
          showNotification(
            "error",
            "Error al crear",
            "No se pudo crear el curso. Por favor, intenta nuevamente."
          );
        }
      } else if (accion === "editar" && selectedCurso) {
        const data = {
          nombre: selectedCurso.nombre,
          anio: selectedCurso.anio,
          anioEscolar: selectedCurso.anioEscolar,
          vigente: selectedCurso.vigente,
        };

        const response = await cursosProfesorService.updateCurso(
          selectedCurso.idCurso,
          data
        );

        if (response.success) {
          await onRefresh();
          cerrarModal();
          showNotification(
            "success",
            "Curso actualizado",
            "Los cambios se han guardado correctamente."
          );
        } else {
          showNotification(
            "error",
            "Error al actualizar",
            "No se pudieron guardar los cambios. Por favor, intenta nuevamente."
          );
        }
      }

      if (accion === "eliminar" && selectedCurso) {
        const response = await cursosProfesorService.deleteCurso(
          selectedCurso.idCurso
        );

        if (response.success) {
          await onRefresh();
          cerrarModal();
          showNotification(
            "success",
            "Curso desactivado",
            "El curso ha sido marcado como no vigente."
          );
        } else {
          showNotification(
            "error",
            "Error al desactivar",
            "No se pudo desactivar el curso. Por favor, intenta nuevamente."
          );
        }
      }
    } catch (error: any) {
      console.error("Error en operación:", error);
      const errorInfo = getErrorMessage(error);
      showNotification("error", errorInfo.title, errorInfo.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (cursos.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
          <svg
            className="w-8 h-8 text-purple-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        </div>
        <p className="text-gray-500 text-lg font-medium">
          No tienes cursos asignados todavía.
        </p>
        <p className="text-gray-400 text-sm mt-1">
          Los cursos aparecerán aquí cuando se te asignen.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-4 flex justify-end">
        <button
          onClick={abrirModalCrear}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-sm hover:shadow-md flex items-center gap-2 font-medium"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Crear Curso
        </button>
      </div>

      <div className="w-full min-w-max overflow-x-auto">
        <table
          className="w-full divide-y divide-gray-200"
          style={{ minWidth: "1200px" }}
        >
          <thead className="bg-gradient-to-r from-purple-50 to-purple-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Nombre Completo
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Nivel
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Curso
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Colegio
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Alumnos
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Vigencia
              </th>
              <th className="px-6 py-4 text-right text-xs font-bold text-purple-700 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {cursos.map((curso) => (
              <tr
                key={curso.idCurso}
                className="hover:bg-purple-50 transition-colors duration-150"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-gray-900">
                    {curso.nombre} - {curso.anio}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">
                    {curso.anioEscolar}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">{curso.nombre}</div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">
                    {curso.colegio.nombre}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-3 py-1 inline-flex text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                    {curso.cantidadAlumnos} alumnos
                  </span>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                      curso.vigente
                        ? "bg-green-100 text-green-800 border border-green-200"
                        : "bg-red-100 text-red-800 border border-red-200"
                    }`}
                  >
                    {curso.vigente ? "Vigente" : "No vigente"}
                  </span>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end items-center gap-2">
                    <button
                      onClick={() => abrirModal(curso, "editar")}
                      className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-sm hover:shadow-md flex items-center gap-1.5"
                      title="Editar curso"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span className="text-xs font-medium">Editar</span>
                    </button>
                    <button
                      onClick={() => abrirModal(curso, "eliminar")}
                      className="px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-sm hover:shadow-md flex items-center gap-1.5"
                      title="Eliminar curso"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      <span className="text-xs font-medium">Eliminar</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalAbierto && (selectedCurso || accion === "crear") && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={cerrarModal}
          ></div>

          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
              <div
                className={`px-6 py-4 flex items-center justify-between ${
                  accion === "eliminar"
                    ? "bg-gradient-to-r from-red-500 to-red-600"
                    : "bg-gradient-to-r from-purple-600 to-purple-700"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    {accion === "eliminar" ? (
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {accion === "crear"
                        ? "Crear Curso"
                        : accion === "editar"
                        ? "Editar Curso"
                        : "Eliminar Curso"}
                    </h2>
                    <p
                      className={`text-sm ${
                        accion === "eliminar"
                          ? "text-red-100"
                          : "text-purple-100"
                      }`}
                    >
                      {accion === "crear"
                        ? "Registra un nuevo curso en el sistema"
                        : accion === "editar"
                        ? "Modifica la información del curso"
                        : "Esta acción no se puede deshacer"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={cerrarModal}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                {accion === "eliminar" && selectedCurso && (
                  <div className="space-y-4">
                    <p className="text-gray-700 text-base">
                      ¿Estás seguro que deseas eliminar el curso{" "}
                      <span className="font-bold text-gray-900">
                        {selectedCurso.nombreCompleto}
                      </span>
                      ?
                    </p>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-800">
                        Esta acción marcará el curso como no vigente pero no lo
                        eliminará definitivamente. El curso quedará marcado como
                        no vigente.
                      </p>
                    </div>
                  </div>
                )}

                {accion === "editar" && selectedCurso && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nombre del Curso
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        value={selectedCurso.nombre}
                        onChange={(e) =>
                          selectedCurso &&
                          setSelectedCurso({
                            ...selectedCurso,
                            nombre: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Año Escolar
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        value={selectedCurso.anioEscolar}
                        onChange={(e) =>
                          selectedCurso &&
                          setSelectedCurso({
                            ...selectedCurso,
                            anioEscolar: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Año
                      </label>
                      <input
                        type="number"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        value={selectedCurso.anio}
                        onChange={(e) =>
                          selectedCurso &&
                          setSelectedCurso({
                            ...selectedCurso,
                            anio: Number(e.target.value),
                          })
                        }
                      />
                    </div>

                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <p className="text-xs text-purple-700 font-medium mb-1">
                        Información adicional
                      </p>
                      <p className="text-sm text-gray-600">
                        Colegio: {selectedCurso.colegio.nombre}
                      </p>
                      <p className="text-sm text-gray-600">
                        Alumnos: {selectedCurso.cantidadAlumnos}
                      </p>
                    </div>

                    {selectedCurso && !selectedCurso.vigente && (
                      <div className="flex items-center space-x-3 pt-2">
                        <input
                          type="checkbox"
                          checked={selectedCurso.vigente}
                          onChange={(e) => {
                            selectedCurso &&
                              setSelectedCurso({
                                ...selectedCurso,
                                vigente: e.target.checked,
                              });
                          }}
                          className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                        />
                        <label className="text-sm font-medium text-gray-700">
                          Vigente
                        </label>
                      </div>
                    )}
                  </div>
                )}

                {accion === "crear" && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nombre del Curso *
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        value={nuevoCurso.nombre}
                        onChange={(e) =>
                          setNuevoCurso({
                            ...nuevoCurso,
                            nombre: e.target.value,
                          })
                        }
                        placeholder="Ej: A, B, C"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Año Escolar *
                      </label>
                      <select
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                        value={nuevoCurso.anioEscolarId || ""}
                        onChange={(e) =>
                          setNuevoCurso({
                            ...nuevoCurso,
                            anioEscolarId: e.target.value
                              ? parseInt(e.target.value)
                              : null,
                          })
                        }
                        required
                      >
                        <option value="">Seleccione un año escolar</option>
                        {aniosEscolares.map((anio) => (
                          <option
                            key={anio.idAnioEscolar}
                            value={anio.idAnioEscolar}
                          >
                            {anio.nombre}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Año *
                      </label>
                      <input
                        type="number"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        value={nuevoCurso.anio}
                        onChange={(e) =>
                          setNuevoCurso({
                            ...nuevoCurso,
                            anio: Number(e.target.value),
                          })
                        }
                        min="1900"
                        max="2100"
                        required
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                <button
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  onClick={cerrarModal}
                >
                  Cancelar
                </button>

                <button
                  onClick={handleConfirmar}
                  className={`px-4 py-2 text-white rounded-lg transition-colors font-medium flex items-center gap-2 ${
                    accion === "crear" || accion === "editar"
                      ? "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
                      : "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                  }`}
                  disabled={isSaving}
                >
                  {accion === "crear" ? (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Crear Curso
                    </>
                  ) : accion === "editar" ? (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Guardar Cambios
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Confirmar Eliminación
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSaving && (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-30 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4">
            <div className="relative w-20 h-20">
              <svg
                className="animate-spin w-20 h-20 text-purple-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
            </div>
            <p className="text-lg font-semibold text-gray-700">
              Guardando cambios...
            </p>
          </div>
        </div>
      )}

      {notification.isOpen && (
        <div
          className={`fixed z-[60] max-w-sm w-full ${
            notification.type === "success"
              ? "bottom-4 right-4 animate-in slide-in-from-bottom-5"
              : "top-4 right-4 animate-in slide-in-from-top-5"
          }`}
        >
          <div
            className={`rounded-xl shadow-2xl p-4 border-l-4 ${
              notification.type === "success"
                ? "bg-green-50 border-green-500 text-green-800"
                : notification.type === "error"
                ? "bg-red-50 border-red-500 text-red-800"
                : notification.type === "warning"
                ? "bg-yellow-50 border-yellow-500 text-yellow-800"
                : "bg-blue-50 border-blue-500 text-blue-800"
            }`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {notification.type === "success" && (
                  <svg
                    className="w-6 h-6 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                {notification.type === "error" && (
                  <svg
                    className="w-6 h-6 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                {notification.type === "warning" && (
                  <svg
                    className="w-6 h-6 text-yellow-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                )}
                {notification.type === "info" && (
                  <svg
                    className="w-6 h-6 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold">{notification.title}</h3>
                <p className="text-sm mt-1 opacity-90">
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() =>
                  setNotification({
                    isOpen: false,
                    type: "success",
                    title: "",
                    message: "",
                  })
                }
                className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
